import {
  useState,
  useEffect,
  useRef,
  useMemo,
  Dispatch,
  SetStateAction,
} from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import {
  GroupHeader,
  GroupItems,
} from '../../../../shared/autocomplete-groups-ui';
import { ReportModel, ReportType, UserTier } from '../../../../../types';
import ScopesAlert from '../../../../shared/scopes-alert';
import AlertList from '../../../../shared/alert-list';
import dayjs from 'dayjs';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../../utils/roles/client-roles';
import { ReportFormValues } from '../reports-form-page';
import { gPanelTiers } from '../../../../shared/gPanel-tiers';
import { Messages } from '../../../../utils/message-constants';
import { useUserSettings } from '../../../../utils/use-user-settings';
import ReportNameEditor from '../../../../shared/editor/report-name-editor';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SharedFilePickerOld from '../../../../shared/shared-file-picker-old';
import SharedFilePicker from '../../../../shared/shared-file-picker';
import { SectionHeader } from '../../../../shared/section-header';
import { isFeatureEnabled } from '../../../../utils/feature-flags';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Fade from '@mui/material/Fade';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import WorkspacePremium from '@mui/icons-material/WorkspacePremium';
import type { PopperPlacementType } from '@mui/material/Popper';
interface StepBasicInformationProps {
  control: Control<ReportFormValues>;
  watch: UseFormWatch<ReportFormValues>;
  trigger: UseFormTrigger<ReportFormValues>;
  setValue: UseFormSetValue<ReportFormValues>;
  clearErrors: UseFormClearErrors<ReportFormValues>;
  errors: FieldErrors<ReportFormValues>;
  reportTypes: ReportType[];
  setSelectedReportType: Dispatch<SetStateAction<ReportType | null>>;
  usersTier: UserTier;
  selectedReportType: ReportType | null;
  toolsRoles: string[];
  apiAccessGranted: boolean | undefined;
  checkingApiAccess: boolean;
  cloudIdentityReportTypes: string[];
  driveLabelsAccessGranted: boolean | undefined;
  checkingDriveLabelsAccess: boolean;
  driveLabelsReportTypes: string[];
  dialogType: 'create' | 'edit';
  reports?: ReportModel[];
  selectedRowId?: number;
  onViewReport: (r: ReportModel) => void;
}

export default function StepBasicInformation({
  control,
  watch,
  trigger,
  setValue,
  clearErrors,
  errors,
  reportTypes,
  setSelectedReportType,
  usersTier,
  selectedReportType,
  toolsRoles,
  apiAccessGranted,
  checkingApiAccess,
  cloudIdentityReportTypes,
  driveLabelsAccessGranted,
  checkingDriveLabelsAccess,
  driveLabelsReportTypes,
  dialogType,
  reports,
  selectedRowId,
  onViewReport,
}: StepBasicInformationProps) {
  const { loggedInUser, features } = useUserSettings();
  const manualRun = watch('manualRun') || false;
  const scheduled = watch('scheduled') || false;
  const runWithoutSaving = watch('runWithoutSaving') || false;
  const reportType = watch('reportType') || '';

  // Duplicate check for report types without a Sources step (hasPossibleSources === false)
  const duplicateReportsNoSources = useMemo(() => {
    if (
      !reports ||
      !reportType ||
      selectedReportType?.hasPossibleSources !== false
    )
      return [];
    return reports.filter((report) => {
      if (report.id === selectedRowId) return false;
      return report.reportType === reportType;
    });
  }, [reports, reportType, selectedRowId, selectedReportType]);
  // State for enterprise WorkspacePremium popper
  const [enterpriseAnchorEl, setEnterpriseAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [enterprisePopperOpen, setEnterprisePopperOpen] = useState(false);
  const [enterprisePlacement, setEnterprisePlacement] =
    useState<PopperPlacementType>();
  const isFirstRender = useRef(true);
  const generatedFileName = watch('generatedFileName');

  // Effect to set default generatedFileName for edit dialog
  useEffect(() => {
    if (
      isFirstRender.current &&
      dialogType === 'edit' &&
      (!generatedFileName || generatedFileName.trim() === '')
    ) {
      isFirstRender.current = false;
      setValue('generatedFileName', '{|ReportType|}_{|DateTime|}');
    }
  }, [dialogType, generatedFileName, setValue]);

  // State for info tooltip (Generated File Name helper)
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );
  const [infoOpen, setInfoOpen] = useState(false);
  const today = dayjs();
  const currentDate = dayjs().format('MM/DD/YYYY');
  const currentTime = dayjs().format('hh:mm:ss A'); // If using time zones
  const currentDateTime = dayjs().format('MM/DD/YYYY hh:mm:ss A');
  const sampleEmail = loggedInUser || '';
  const reportTypeLabel = watch('reportName') || 'ReportType';

  const acceptableSourceTypes = [
    'User',
    'OrgUnit',
    'Domain',
    'Group',
    'gPanel_Role',
    'Resource_Calendar',
    'Shared_Drive',
  ];

  const enterpriseReportNames = [
    'activitiesSharedReportMyDrive',
    'activitiesSharedReportPublic',
    'activitiesSharedReportSharedDrive',
    'activitiesSharedReportPublicSharedDrives',
  ];

  const isEnterpriseReport = (reportName: string) => {
    return enterpriseReportNames.includes(reportName);
  };

  const handleEnterpriseClick =
    (newPlacement: PopperPlacementType) =>
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setEnterpriseAnchorEl(event.currentTarget);
      setEnterprisePopperOpen(
        (prev) => enterprisePlacement !== newPlacement || !prev
      );
      setEnterprisePlacement(newPlacement);
    };

  const handleEnterpriseClickAway = () => {
    setEnterprisePopperOpen(false);
  };

  function getToolTipText(option: ReportType) {
    if (
      usersTier.rank <= gPanelTiers.starter.rank &&
      !option.includeInStarter
    ) {
      return Messages.Errors.Tier.Acting(gPanelTiers.standard.displayValue);
    } else if (
      !isAbleToSeeUIElement(toolsRoles, ToolsRoles.MaunalRunReport.value) &&
      !isAbleToSeeUIElement(toolsRoles, option.role)
    ) {
      const matchingRole = Object.keys(ToolsRoles).find(
        (key) => ToolsRoles[key].value === option.role
      );
      if (matchingRole) {
        return Messages.Errors.Permission.Acting(ToolsRoles[matchingRole].name);
      }
      return Messages.Errors.Permission.Acting(option.role);
    } else {
      return option.description;
    }
  }
  const replaceTagsInFileName = (template: string) => {
    return template
      .replace(/{\|ReportType\|}/g, reportTypeLabel)
      .replace(/{\|DateTime\|}/g, currentDateTime)
      .replace(/{\|Date\|}/g, currentDate)
      .replace(/{\|Time\|}/g, currentTime)
      .replace(/{\|UserEmail\|}/g, sampleEmail);
  };

  const destinationFolder = watch('destFolderName') || '';
  const destinationFolderId = watch('destFolderId') || '';

  const clearConfigFields = () => {
    setValue('dateRange', [dayjs().subtract(7, 'day'), dayjs()]);
    setValue('sources', []);
    setValue('allSource', false);
    setValue('inactiveSinceDate', 0);
    setValue('regexTypeSelectedList', []);
    setValue('regex', null);
    setValue('externalEmailAddress', null);
    setValue('startDate', 0);
    setValue('endDate', 0);
    setValue('reportOnLastXDays', 0);
    setValue('xDaysSelected', false);
    setValue('daysback', null);
    setValue('emailsToNotify', []);
    setValue('permissions', []);
    clearErrors();
  };

  return (
    <Stack alignItems={'left'} width={'100%'} gap={4}>
      <Stack gap={1}>
        <SectionHeader description="Please choose your report type and provide a report name. You can also choose whether this report should be run manually or on a schedule.">
          Basic Information
        </SectionHeader>

        <Stack>
          <Stack flexDirection="row" gap={1} sx={{ mb: 1 }}>
            <Controller
              name="reportType"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  autoHighlight
                  autoComplete
                  id="reportType"
                  options={reportTypes.sort((a, b) =>
                    a.category.localeCompare(b.category)
                  )}
                  sx={{ width: 450, pb: 1 }}
                  groupBy={(option) => option.category}
                  getOptionLabel={(option) => option.niceName || ''}
                  value={selectedReportType}
                  onChange={(_event, value) => {
                    if (value) {
                      setValue('reportName', value.niceName);
                      setValue('description', value.description);
                      const currentGenFileName = watch('generatedFileName');
                      if (
                        !currentGenFileName ||
                        currentGenFileName.trim() === ''
                      ) {
                        setValue(
                          'generatedFileName',
                          '{|ReportType|}_{|DateTime|}'
                        );
                      }
                      if (
                        selectedReportType?.hasDateRange === false ||
                        selectedReportType?.hasDateSelection === false
                      ) {
                        setValue('dateRange', [
                          today.subtract(7, 'day'),
                          today,
                        ]);
                      }
                      if (value.hasPossibleSources) {
                        const sourceTypes = value.possibleSources.filter(
                          (source) => acceptableSourceTypes.includes(source)
                        );
                        if (
                          dialogType === 'create' &&
                          value.name === 'sharedDrivesDocsSharedExternally'
                        ) {
                          setValue('sourceType', 'Shared_Drive' as const);
                        } else if (sourceTypes.length > 0) {
                          setValue(
                            'sourceType',
                            sourceTypes[0] as
                              | 'User'
                              | 'Group'
                              | 'OrgUnit'
                              | 'Domain'
                              | 'gPanel_Role'
                              | 'Resource_Calendar'
                              | 'Shared_Drive'
                          );
                        }
                      } else {
                        setValue('sourceType', null);
                      }
                      field.onChange(value!.name);
                      setValue('columns', value.columns);
                      clearConfigFields();
                      setSelectedReportType(value);
                    } else {
                      setValue('reportType', '');
                      setValue('columns', []);
                      setValue('sources', []);
                      setValue('daysback', null);
                      setValue('sourceType', null);
                      setValue('regexTypeSelectedList', []);
                      setValue('reportName', '');
                      setValue('description', '');
                      setValue('generatedFileName', '');
                      setSelectedReportType(null);
                    }
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.name === value?.name
                  }
                  getOptionDisabled={(option) =>
                    getToolTipText(option) !== option.description
                  }
                  renderOption={(props, option) => (
                    <Tooltip
                      key={option.name}
                      title={getToolTipText(option)}
                      placement="right"
                      arrow
                    >
                      <span>
                        <ListItem {...props} key={option.name}>
                          {option.niceName}
                          {isEnterpriseReport(option.name) && (
                            <ListItemIcon sx={{ marginLeft: 'auto' }}>
                              <WorkspacePremium color="primary" />
                            </ListItemIcon>
                          )}
                        </ListItem>
                      </span>
                    </Tooltip>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Report Type"
                      required
                      error={!!errors.reportType}
                      helperText={
                        !errors?.reportType?.message &&
                        selectedReportType?.name ===
                          'sharedDrivesDocsSharedExternally'
                          ? 'Only one report of this type can be run at once.'
                          : errors?.reportType?.message
                          ? errors?.reportType?.message
                          : ' '
                      }
                    />
                  )}
                  renderGroup={(params) => {
                    let groupName =
                      params.group.charAt(0).toUpperCase() +
                      params.group.slice(1);
                    if (params.group === 'gpanel') {
                      groupName = 'gPanel';
                    }
                    return (
                      <li key={params.key}>
                        <GroupHeader>{groupName}</GroupHeader>
                        <GroupItems>{params.children}</GroupItems>
                      </li>
                    );
                  }}
                />
              )}
            />
            <Stack direction="row" spacing={1}>
              <ClickAwayListener onClickAway={handleEnterpriseClickAway}>
                <Box sx={{ position: 'relative' }}>
                  <Popper
                    style={{ zIndex: 2000 }}
                    open={enterprisePopperOpen}
                    anchorEl={enterpriseAnchorEl}
                    placement="right-start"
                    transition
                  >
                    {({ TransitionProps }) => (
                      <Fade {...TransitionProps} timeout={350}>
                        <Paper sx={{ width: 450 }}>
                          {usersTier.rank < gPanelTiers.enterprise.rank ? (
                            <>
                              <Typography sx={{ p: 2 }} variant="body1">
                                You are not currently an enterprise user; you
                                only have access to 6 months of activities. If
                                you want access to the complete history of
                                activities you can upgrade from your current
                                plan today.
                              </Typography>
                              <Typography sx={{ p: 2 }} variant="caption">
                                Please contact your CSM to review all your
                                upgrade options.{' '}
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Typography sx={{ px: 2, pt: 2 }} variant="body1">
                                As an enterprise user, you have full access to
                                over 6 months of activities.
                              </Typography>
                              <ul>
                                {reportTypes
                                  .filter((r) => isEnterpriseReport(r.name))
                                  .map((report) => (
                                    <li key={report.name}>{report.niceName}</li>
                                  ))}
                              </ul>
                            </>
                          )}
                        </Paper>
                      </Fade>
                    )}
                  </Popper>
                  <IconButton onClick={handleEnterpriseClick('bottom-end')}>
                    <WorkspacePremium color="primary" />
                  </IconButton>
                </Box>
              </ClickAwayListener>

              {checkingApiAccess && (
                <Stack direction="row" gap={1} pt={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    Checking data access...
                  </Typography>
                  <CircularProgress size={20} />
                </Stack>
              )}
            </Stack>
          </Stack>
          {duplicateReportsNoSources.length > 0 && (
            <AlertList
              severity="info"
              items={duplicateReportsNoSources}
              itemsThresholdBeforeScroll={4}
              sx={{ mt: -3, mb: 3 }}
              message={
                <Typography>
                  We found {duplicateReportsNoSources.length}{' '}
                  {duplicateReportsNoSources.length === 1
                    ? 'report'
                    : 'reports'}{' '}
                  with the same report type.
                </Typography>
              }
              renderItem={(report: ReportModel) => (
                <Box
                  key={report.id}
                  sx={{
                    p: 1,
                    width: '100%',
                    backgroundColor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography
                      fontWeight="bold"
                      noWrap
                      sx={{ flexGrow: 1, textOverflow: 'ellipsis' }}
                    >
                      {report.reportName}
                    </Typography>
                    <Link
                      component="button"
                      variant="body2"
                      type="button"
                      sx={{ flexShrink: 0 }}
                      onClick={() => onViewReport(report)}
                    >
                      View Report
                    </Link>
                  </Stack>
                </Box>
              )}
            />
          )}
          <Stack spacing={2} sx={{ mb: 1 }}>
            <Controller
              name="reportName"
              control={control}
              render={({ field }) => (
                <TextField
                  required
                  inputProps={{
                    id: 'reportName',
                  }}
                  InputLabelProps={{
                    htmlFor: 'reportName',
                  }}
                  label="Report Name"
                  sx={{ width: 450 }}
                  {...field}
                  error={!!errors.reportName}
                  helperText={
                    'This will be the name that will appear in the grid on the Reports Page(Defaults to Report Type).'
                  }
                />
              )}
            />
            <Stack
              direction="row"
              spacing={2}
              alignItems="flex-start"
              flexWrap="wrap"
            >
              <Controller
                name="generatedFileName"
                control={control}
                render={({ field }) => {
                  const handleChange = (val: string) => {
                    field.onChange(val);
                    trigger('generatedFileName');
                    const fileNameEl = document.getElementById('fileName');
                    if (fileNameEl) {
                      fileNameEl.textContent = val.trim()
                        ? replaceTagsInFileName(val)
                        : `${reportTypeLabel}_${currentDateTime}`;
                    }
                  };

                  return (
                    <ReportNameEditor
                      labelText="Generated File Name"
                      required
                      content={field.value}
                      setContent={handleChange}
                      error={!!errors.generatedFileName}
                      helperText={
                        'This will be name of the generated report after the report runs. By default the report is formatted as {|ReportType|}_{|DateTime|}'
                      }
                      mergeTags={[
                        { title: 'ReportType', value: 'ReportType' },
                        { title: 'UserEmail', value: 'UserEmail' },
                        { title: 'DateTime', value: 'DateTime' },
                        { title: 'Date', value: 'Date' },
                        { title: 'Time', value: 'Time' },
                      ]}
                      onBlur={field.onBlur}
                    />
                  );
                }}
              />

              <ClickAwayListener onClickAway={() => setInfoOpen(false)}>
                <Box sx={{ position: 'relative' }}>
                  <IconButton
                    onClick={(e) => {
                      setInfoAnchorEl(e.currentTarget);
                      setInfoOpen((prev) => !prev);
                    }}
                  >
                    <InfoOutlinedIcon color="primary" />
                  </IconButton>
                  <Popper
                    open={infoOpen}
                    anchorEl={infoAnchorEl}
                    placement="right-start"
                    transition
                    disablePortal
                    modifiers={[
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 8],
                        },
                      },
                    ]}
                    style={{ zIndex: 2000 }}
                  >
                    {({ TransitionProps }) => (
                      <Fade {...TransitionProps} timeout={350}>
                        <Paper
                          sx={{
                            p: 2,
                            width: 525,
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            Use these variables to create a dynamic file name
                            for your report. Each variable will be automatically
                            replaced with the correct information when the
                            report is created.
                          </Typography>
                          <ul
                            style={{
                              marginTop: 0,
                              paddingLeft: '20px',
                              fontSize: '0.850rem',
                            }}
                          >
                            <li>
                              <code>{'{|Date|}'}</code> – {currentDate}
                            </li>
                            <li>
                              <code>{'{|Time|}'}</code> – {currentTime}
                            </li>
                            <li>
                              <code>{'{|Datetime|}'}</code> – {currentDateTime}
                            </li>
                            <li>
                              <code>{'{|UserEmail|}'}</code> – {sampleEmail}
                            </li>
                            <li>
                              <code>{'{|ReportType|}'}</code> –{' '}
                              {reportTypeLabel}
                            </li>
                          </ul>
                          <Typography
                            variant="body2"
                            sx={{ mt: 2, fontSize: '0.850rem' }}
                          >
                            Generated File Name:
                            <br />
                            <code id="fileName">
                              {generatedFileName.trim()
                                ? replaceTagsInFileName(generatedFileName)
                                : `${reportTypeLabel}_${currentDateTime}`}
                            </code>
                          </Typography>
                        </Paper>
                      </Fade>
                    )}
                  </Popper>
                </Box>
              </ClickAwayListener>
            </Stack>
          </Stack>

          {selectedReportType?.name !== 'hangoutsMeet' ? (
            <Stack flexDirection={'column'} gap={2} mt={1}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputProps={{
                      id: 'description',
                    }}
                    InputLabelProps={{
                      htmlFor: 'description',
                    }}
                    multiline
                    rows={2}
                    label="Report Description"
                    sx={{ width: 450 }}
                  />
                )}
              />
              <Stack flexDirection={'row'} gap={2}>
                <Controller
                  name="manualRun"
                  control={control}
                  render={({ field: props }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...props}
                          checked={props.value}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            props.onChange(checked);
                            if (checked) {
                              setValue('scheduled', false);
                              setValue('runWithoutSaving', false);
                              setValue('dates', []);
                              setValue('times', []);
                              setValue('scheduleEndDate', 0);
                            } else {
                              if (!runWithoutSaving && !scheduled) {
                                setValue('scheduled', true);
                              }
                            }
                          }}
                        />
                      }
                      label="Manual Run"
                    />
                  )}
                />
                <Controller
                  name="scheduled"
                  control={control}
                  render={({ field: props }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...props}
                          checked={props.value}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            props.onChange(checked);
                            if (checked) {
                              setValue('manualRun', false);
                              setValue('runWithoutSaving', false);
                              setValue('dates', []);
                              setValue('times', []);
                              setValue('scheduleEndDate', 0);
                            } else {
                              if (!runWithoutSaving && !manualRun) {
                                setValue('manualRun', true);
                              }
                            }
                          }}
                        />
                      }
                      label="Set Schedule"
                    />
                  )}
                />
                {dialogType === 'create' && (
                  <Controller
                    name="runWithoutSaving"
                    control={control}
                    render={({ field: props }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...props}
                            checked={props.value}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              props.onChange(checked);
                              if (checked) {
                                setValue('manualRun', false);
                                setValue('scheduled', false);
                                setValue('dates', []);
                                setValue('times', []);
                                setValue('scheduleEndDate', 0);
                              } else {
                                if (!manualRun && !scheduled) {
                                  setValue('manualRun', true);
                                }
                              }
                            }}
                          />
                        }
                        label="Run Without Saving"
                      />
                    )}
                  />
                )}
              </Stack>
            </Stack>
          ) : (
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  inputProps={{
                    id: 'description',
                  }}
                  InputLabelProps={{
                    htmlFor: 'description',
                  }}
                  label="Description"
                  sx={{ width: 450 }}
                />
              )}
            />
          )}
        </Stack>
        {cloudIdentityReportTypes.includes(reportType) &&
          !apiAccessGranted &&
          !checkingApiAccess && (
            <ScopesAlert
              affectedColumns={['Created Date']}
              apiKey={'cloudIdentity'}
              isReportsOrBulkOps
            />
          )}
        {driveLabelsReportTypes.includes(reportType) &&
          !driveLabelsAccessGranted &&
          !checkingDriveLabelsAccess && (
            <ScopesAlert
              apiKey={'driveLabels'}
              additionalText="This report cannot resolve or include labels until access is granted."
            />
          )}
      </Stack>
      <Stack gap={1} mb={3}>
        <SectionHeader description="Optionally provide a destination folder for your report to be placed in once it has been run. If none is provided, the report will be in the root of your My Drive.">
          Destination Folder
        </SectionHeader>
        {isFeatureEnabled('drivePickerUIReact', features) ? (
          <SharedFilePicker
            pickerType="folder"
            selectedFileName={destinationFolder}
            selectedFileId={destinationFolderId}
            onFileSelected={({ id, name, url, mimeType }) => {
              setValue('destFolderId', id);
              setValue('destFolderName', name);
              setValue('destFolderUrl', url);
              setValue('destFolderMimeType', mimeType);
            }}
            onFileRemoved={() => {
              setValue('destFolderId', null);
              setValue('destFolderName', null);
              setValue('destFolderUrl', null);
              setValue('destFolderMimeType', null);
            }}
            includeSharedDrives
          />
        ) : (
          <SharedFilePickerOld
            pickerType="folder"
            selectedFileName={destinationFolder}
            selectedFileId={destinationFolderId}
            onFileSelected={({ id, name, url, mimeType }) => {
              setValue('destFolderId', id);
              setValue('destFolderName', name);
              setValue('destFolderUrl', url);
              setValue('destFolderMimeType', mimeType);
            }}
            onFileRemoved={() => {
              setValue('destFolderId', null);
              setValue('destFolderName', null);
              setValue('destFolderUrl', null);
              setValue('destFolderMimeType', null);
            }}
            includeSharedDrives
          />
        )}
      </Stack>
    </Stack>
  );
}
